import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { useMessage } from "../../context/useMessageContext"
import { DownloadFileRequest, GetRequest, PostFormRequest } from "../../services/ApiClientService"
import type { DataImportFormat } from "../../Objects/DataImport/DataImportFormat"
import type { DataImportTarget } from "../../Objects/DataImport/DataImportTarget"
import type { DataImportPreview } from "../../Objects/DataImport/DataImportPreview"
import type { DataImportSchema } from "../../Objects/DataImport/DataImportSchema"
import { useUpgradePlan } from "../../context/useUpgradePlanContext"
import { isPlanLimitError } from "../../utils/planLimits"

export const useDataImport = () => {
  const { t } = useTranslation("dataImport")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const { setMessage } = useMessage()

  const { openPlan } = useUpgradePlan()

  const handleGetSchema = async (target: DataImportTarget, onSuccess: (schema: DataImportSchema) => void) => {
    try {
      enableLoad()

      const response = await GetRequest<DataImportSchema>(API_ROUTES.DATA_IMPORT.SCHEMA(target))

      if (response.success && response.data) {
        onSuccess(response.data)

        return
      }

      throw new Error(response.message)
    } catch (e) {
      const message = e instanceof Error ? e.message : t("general:label_unknown_error_description")

      setError(t("general:label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleDownloadTemplate = async (target: DataImportTarget, format: DataImportFormat) => {
    try {
      enableLoad()

      await DownloadFileRequest(
        API_ROUTES.DATA_IMPORT.TEMPLATE(target, format),
        t("template_file_name", { target, format }),
      )
    } catch (e) {
      const message = e instanceof Error ? e.message : t("general:label_unknown_error_description")

      setError(t("general:label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleValidateImport = async (
    target: DataImportTarget,
    file: File | null,
    onSuccess: (preview: DataImportPreview) => void,
  ) => {
    if (!file) {
      setError(t("general:label_title_erro"), t("error_file_required"))

      return
    }

    try {
      enableLoad()

      const response = await PostFormRequest<DataImportPreview>(API_ROUTES.DATA_IMPORT.VALIDATE(target), { File: file })

      if (response.success && response.data) {
        onSuccess(response.data)

        return
      }

      throw new Error(response.message)
    } catch (e) {
      const message = e instanceof Error ? e.message : t("general:label_unknown_error_description")

      setError(t("general:label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleCommitImport = async (
    target: DataImportTarget,
    file: File | null,
    selectedRowNumbers: number[],
    onSuccess: (preview: DataImportPreview) => void,
  ) => {
    if (!file) {
      setError(t("general:label_title_erro"), t("error_file_required"))

      return
    }

    try {
      enableLoad()

      const response = await PostFormRequest<DataImportPreview>(API_ROUTES.DATA_IMPORT.COMMIT(target), {
        File: file,
        SelectedRowNumbers: selectedRowNumbers,
      })

      if (response.success && response.data) {
        setMessage(t("success_title"), t("success_description", { count: response.data.importedRows }))

        onSuccess(response.data)

        return
      }

      throw new Error(response.message)
    } catch (e) {
      const message = e instanceof Error ? e.message : t("general:label_unknown_error_description")

      if (isPlanLimitError(message)) {
        openPlan()
      }

      setError(t("general:label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  return {
    handleGetSchema,
    handleDownloadTemplate,
    handleValidateImport,
    handleCommitImport,
  }
}
